import { CClient, flattenErrorsDictValues } from '@common/utils'
import {
  validateIdentifier,
  validatePassword,
} from '@containers/common/validators'
import { getAPIErrorString, isEmptyObject } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import IdentifierInput from './identifier-input'
import DottedCardBackgroundWrapper from '../common/dotted-card-bg'
import PasswordInput from './password-input'
import LoginButton from './password-login-btn'
import TestimonialCard from './testimonial-card'
import { t, Trans } from '@lingui/macro'
import {
  decodePhoneQueryValue,
  encodePhoneQueryValue,
} from '@lib/utils/phone-query'
import {
  buildIdentifierBody,
  detectIdentifierMode,
  IdentifierMode,
} from '@lib/utils/identifier'

const TestimonialCardWithDottedBackground = DottedCardBackgroundWrapper(
  TestimonialCard
)

const PasswordLoginScreen = () => {
  const router = useRouter()
  const phoneFromQuery = (router.query?.ph || router.query?.phone) as string
  const decodedPhoneFromQuery = useMemo(
    () => decodePhoneQueryValue(phoneFromQuery),
    [phoneFromQuery]
  )

  const [password, setPassword] = useState('')
  // `phone` holds whichever identifier was typed - a phone number (dial
  // code, no plus) or an email address; `mode` says which.
  const [phone, setPhone] = useState('')
  const [mode, setMode] = useState<IdentifierMode>('phone')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    if (!router.isReady) return
    if (decodedPhoneFromQuery) {
      setPhone(decodedPhoneFromQuery)
      setMode(detectIdentifierMode(decodedPhoneFromQuery))
    }
    if (router.query.phone) {
      const { phone: rawPhoneParam, ...rest } = router.query
      router.replace({ query: { ...rest, ph: rawPhoneParam } }, undefined, {
        shallow: true,
      })
    }
  }, [decodedPhoneFromQuery, router.isReady])

  const handleForgotPassword = () => {
    const encodedPhone = encodePhoneQueryValue(phone)
    const redirectUrl = encodedPhone
      ? `/login/forgot-password/send-otp/?ph=${encodedPhone}`
      : '/login/forgot-password/send-otp/'
    router.push(redirectUrl)
  }

  const handlePasswordLogin = async () => {
    setErrors([])
    setLoading(true)
    try {
      const passwordErrors = validatePassword(password)
      const phoneErrors = validateIdentifier(phone, mode)
      if (!isEmptyObject(passwordErrors) || !isEmptyObject(phoneErrors)) {
        setLoading(false)
        setErrors(Object.values({ ...passwordErrors, ...phoneErrors }))
        return
      }

      const apiEndpoint = '/api/v1/auth/password-login/'
      const apiBody = {
        ...buildIdentifierBody(phone, mode),
        password: password,
      }
      const response = await CClient(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiBody),
      })

      if (!response.ok) {
        const errors = await response.json()
        const errorDict = flattenErrorsDictValues(errors)
        setErrors(errorDict)
        if (response.status >= 400 && response.status < 500) {
          return
        }
        throw new Error(await getAPIErrorString(response, apiEndpoint, apiBody))
      }
      const next = router.query.next?.toString()
      const redirectUrl = next ? next : '/dashboard/'

      const query = router.query
      delete query.next
      delete query.phone
      delete query.ph
      router.push({
        pathname: redirectUrl,
        query,
      })
    } catch (error) {
      captureException(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-start justify-between flex-1 h-full bg-white lg:items-center lg:bg-transparent">
      <div className="container mx-auto">
        <div className="flex flex-wrap items-center">
          <div className="hidden w-full pr-5 lg:block sm:w-1/2">
            <TestimonialCardWithDottedBackground />
          </div>
          <div className="w-full px-4 lg:w-1/2">
            <div className="px-5">
              <div className="leading-tight text-grey text-5xl font-700">
                <p>
                  <Trans>Login with password</Trans>
                </p>
              </div>
              <div className="max-w-md my-5 mt-10">
                <div>
                  <label className="mb-1 text-grey font-500">
                    <Trans>Enter your phone number or email</Trans>
                  </label>
                  <IdentifierInput
                    value={phone}
                    mode={mode}
                    onChange={(value, nextMode) => {
                      setPhone(value)
                      setMode(nextMode)
                    }}
                    onEnter={handlePasswordLogin}
                    autoFocus
                  />
                </div>
                <div className="mt-5">
                  <label className="block mb-1 font-500 text-grey">
                    <Trans>Enter your password</Trans>
                  </label>
                  <PasswordInput
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onEnter={handlePasswordLogin}
                  />
                  <div className="text-right">
                    <button
                      className="text-sm text-right mt-2 text-orange-600 font-500 focus:outline-none"
                      onClick={handleForgotPassword}
                    >
                      <Trans>Forgot password?</Trans>
                    </button>
                  </div>
                </div>
                {errors.length > 0 &&
                  errors.map((error, index) => (
                    <div
                      className="flex items-center space-x-2 bg-red-50 border-l-4 border-orange py-1 mt-2"
                      key={index}
                    >
                      <AlertTriangle size={15} className="text-red-200 ml-2" />
                      <p className="text-sm inline-block">{error}</p>
                    </div>
                  ))}
                <div className="flex space-x-5 mt-10">
                  <LoginButton
                    isDisabled={loading || !phone}
                    isLoading={loading}
                    label={t`Login with password`}
                    handleClick={handlePasswordLogin}
                    isPrimary
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-center w-full h-auto my-16 lg:hidden lg:w-1/2 md:px-5">
              <TestimonialCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordLoginScreen
